/**
 * Global mouse follower cursor (keeps native cursor visible).
 * - Follows pointer on fine-pointer devices (hidden unless pipeline label mode)
 * - Pipeline rows: large label cursor + helper text
 * - Uses lerp for smooth movement
 */
export class MfCursor {
    constructor() {
        this.el = null;
        this.innerEl = null;
        this.textEl = null;
        this._active = false;
        this._rafId = null;

        this._x = -100;
        this._y = -100;
        this._tx = -100;
        this._ty = -100;

        // lerp factor (0..1)
        this._ease = 0.18;

        this._labelTarget = null;
        this._onMove = this._onMove.bind(this);
        this._tick = this._tick.bind(this);
        this._onOver = this._onOver.bind(this);
        this._onOut = this._onOut.bind(this);
        this._onDown = this._onDown.bind(this);
        this._onUp = this._onUp.bind(this);
    }

    init() {
        // Only enable on desktops / fine pointers
        const canUseFinePointer =
            window.matchMedia &&
            window.matchMedia("(pointer: fine)").matches &&
            window.matchMedia("(hover: hover)").matches;
        if (!canUseFinePointer) return;

        // Respect reduced motion (still show, but no RAF loop)
        const reduceMotion =
            window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        this._ensureEl();

        window.addEventListener("mousemove", this._onMove, { passive: true });
        document.addEventListener("mouseover", this._onOver, { passive: true });
        document.addEventListener("mouseout", this._onOut, { passive: true });
        document.addEventListener("pointerdown", this._onDown, { passive: true });
        document.addEventListener("pointerup", this._onUp, { passive: true });

        this._active = true;
        this.el.classList.add("is-ready");

        if (!reduceMotion) {
            this._rafId = window.requestAnimationFrame(this._tick);
        } else {
            // Reduced motion: snap follow using move events only
            this._ease = 1;
        }
    }

    _ensureEl() {
        if (this.el) return;

        const el = document.createElement("div");
        el.className = "no-mf-cursor";
        el.setAttribute("aria-hidden", "true");
        el.innerHTML = `
            <div class="no-mf-cursor__inner">
                <div class="no-mf-cursor__text"></div>
            </div>
        `;
        document.body.appendChild(el);
        this.el = el;
        this.innerEl = el.querySelector(".no-mf-cursor__inner");
        this.textEl = el.querySelector(".no-mf-cursor__text");
    }

    _isInteractive(target) {
        if (!(target instanceof Element)) return false;
        return Boolean(target.closest("a, button"));
    }

    _getLabelTarget(target) {
        if (!(target instanceof Element)) return null;
        const row = target.closest("[data-pipeline-row]");
        if (!row) return null;
        const txt =
            row.getAttribute("data-cursor-text") ||
            row.getAttribute("data-pipeline-cursor-text") ||
            "";
        return txt.trim() !== "" ? row : null;
    }

    _getLabelText(labelTarget) {
        if (!(labelTarget instanceof Element)) return "";
        return (
            labelTarget.getAttribute("data-cursor-text") ||
            labelTarget.getAttribute("data-pipeline-cursor-text") ||
            ""
        ).trim();
    }

    _setLabel(labelTarget) {
        if (!this.el || !this.textEl) return;

        if (!labelTarget) {
            this._labelTarget = null;
            this.textEl.textContent = "";
            this.el.classList.remove("is-label");
            document.body.classList.remove("is-mf-cursor-label");
            return;
        }

        const txt = this._getLabelText(labelTarget).trim();
        if (txt === "") return;

        this._labelTarget = labelTarget;
        this.textEl.textContent = txt;
        this.el.classList.add("is-label");
        document.body.classList.add("is-mf-cursor-label");
    }

    _onMove(e) {
        this._tx = e.clientX;
        this._ty = e.clientY;

        // If reduced motion sets ease=1, we still need to update position.
        if (this._ease === 1 && this.el) {
            this._x = this._tx;
            this._y = this._ty;
            this.el.style.transform = `translate3d(${this._x}px, ${this._y}px, 0)`;
        }
    }

    _onOver(e) {
        if (!this.el) return;

        const labelTarget = this._getLabelTarget(e.target);
        if (labelTarget) {
            this._setLabel(labelTarget);
            return;
        }

        if (this._isInteractive(e.target)) {
            this.el.classList.add("is-hover");
        }
    }

    _onOut(e) {
        if (!this.el) return;

        // leaving label target?
        if (this._labelTarget) {
            const nextLabel = this._getLabelTarget(e.relatedTarget);
            if (!nextLabel || nextLabel !== this._labelTarget) {
                this._setLabel(null);
            }
        }

        if (!this._isInteractive(e.relatedTarget)) {
            this.el.classList.remove("is-hover");
        }
    }

    _onDown(e) {
        if (!this.el) return;
        if (this._labelTarget) {
            this.el.classList.add("is-down");
            return;
        }
        if (this._isInteractive(e.target)) {
            this.el.classList.add("is-down");
        }
    }

    _onUp() {
        if (!this.el) return;
        this.el.classList.remove("is-down");
    }

    _tick() {
        if (!this.el) return;

        this._x += (this._tx - this._x) * this._ease;
        this._y += (this._ty - this._y) * this._ease;
        this.el.style.transform = `translate3d(${this._x}px, ${this._y}px, 0)`;

        if (this._active) {
            this._rafId = window.requestAnimationFrame(this._tick);
        }
    }
}

